import { useEffect } from 'react';
import { Analytics } from '../common/utils/analytics';
import { getItem } from '../helpers/storage';
import type { IUser } from '../interfaces/user/user';

interface NamedEntity {
    name: string;
}

/**
 * Sends a Mixpanel "PAGE VIEWED" event when the brand, hotel and user are all
 * available. Call this directly outside of function components (e.g. from a
 * click handler); prefer `useTrackPageView` on mounted pages.
 */
export const trackPageView = (
    eventName: string,
    selectedBrand?: NamedEntity,
    selectedHotel?: NamedEntity
) => {
    const user = (getItem('user') as { user?: IUser } | undefined)?.user;

    if (!eventName || !selectedBrand || !selectedHotel || !user) {
        return;
    }

    Analytics.sendEvent(eventName, {
        brandName: selectedBrand.name,
        hotelName: selectedHotel.name,
        userId: user.id,
    });
};

/**
 * Fires a Mixpanel "PAGE VIEWED" event once the brand, hotel and user are all
 * available. Shared across brand report pages to avoid duplicating the tracking
 * boilerplate.
 */
export const useTrackPageView = (
    eventName: string,
    selectedBrand?: NamedEntity,
    selectedHotel?: NamedEntity
) => {
    useEffect(() => {
        trackPageView(eventName, selectedBrand, selectedHotel);
    }, [eventName, selectedBrand, selectedHotel]);
};
